<template>
  <div ref="tipTapToolbarContainer" class="rs-partial-tip-tap-toolbar">
    <!-- Full Editor -->
    <div v-if="format === 'full'" class="tip-tap-controls">
      <div class="row mx-0 align-items-center px-1">
        <button
          :disabled="!editor.can().chain().focus().toggleBold().run()"
          :class="{ 'is-active': editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()"
        >
          <i class="far fa-bold" />
        </button>
        <button
          :disabled="!editor.can().chain().focus().toggleItalic().run()"
          :class="{ 'is-active': editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <i class="far fa-italic" />
        </button>
        <button
          :disabled="!editor.can().chain().focus().toggleUnderline().run()"
          :class="{ 'is-active': editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          <i class="far fa-underline" />
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button
          :disabled="!editor.can().chain().focus().toggleStrike().run()"
          :class="{ 'is-active': editor.isActive('strike') }"
          @click="editor.chain().focus().toggleStrike().run()"
        >
          <i class="far fa-strikethrough" />
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button
          :class="{ 'is-active': !editor.isActive({ textAlign: 'center' }) && !editor.isActive({ textAlign: 'right' }) }"
          @click="editor.chain().focus().unsetTextAlign().run()"
        >
          <i class="far fa-align-left" />
        </button>

        <button
          :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }"
          @click="editor.chain().focus().toggleTextAlign('center').run()"
        >
          <i class="far fa-align-center" />
        </button>

        <button
          :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }"
          @click="editor.chain().focus().toggleTextAlign('right').run()"
        >
          <i class="far fa-align-right" />
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button @click="editor.chain().focus().clearNodes().run(); editor.chain().focus().unsetAllMarks().run()">
          <i class="far fa-remove-format" />
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button @click="editor.chain().focus().setHorizontalRule().run()">
          <i class="far fa-horizontal-rule" />
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button
          :class="{ 'is-active': editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
        >
          <i class="far fa-list-ol" />
        </button>

        <button
          :class="{ 'is-active': editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
        >
          <i class="far fa-list" />
        </button>

        <button
          :class="{ 'is-active': editor.isActive('blockquote') }"
          @click="editor.chain().focus().toggleBlockquote().run()"
        >
          <i class="far fa-quote-right" />
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button :disabled="!hasSelection" @click="handleNewLink()">
          <i class="far fa-link" />
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button :disabled="activeStates.includes('table')" @click="handleAddImage">
          <i class="far fa-image" />
        </button>

        <button :disabled="activeStates.includes('table')" @click="handleAddTable">
          <i class="far fa-table" />
        </button>
      </div>

      <div class="row mx-0 mt-0 align-items-center px-1 pb-1">
        <BFormSelect
          v-model="currentFont"
          size="sm"
          :options="fontFamilies"
          :style="{ width: '110px', height: '26px' }"
          class="py-0 mx-1"
          @change="handleFontFamilyChange"
        />

        <BFormSelect
          v-model="currentFormat"
          size="sm"
          :options="textFormats"
          :style="{ width: '110px', height: '26px' }"
          class="py-0 mx-1"
          @change="handleTextFormatChange"
        />

        <span class="separator border-right mx-1">&nbsp;</span>

        <button id="source_mode" :class="{ 'rs-text-primary': isSourceMode }" @click="handleSourceModeToggle">
          <i class="far fa-code mr-1" />Source
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button :disabled="!editor.can().chain().focus().undo().run()" @click="editor.chain().focus().undo().run()">
          <i class="far fa-undo" />
        </button>
        <button :disabled="!editor.can().chain().focus().redo().run()" @click="editor.chain().focus().redo().run()">
          <i class="far fa-redo" />
        </button>

        <span v-if="!isFullScreen" class="separator border-right mx-1">&nbsp;</span>

        <button v-if="!isFullScreen" @click="handleMaximize">
          <i class="far fa-maximize" />
        </button>
      </div>
    </div>

    <!-- Basic Editor -->
    <div v-else-if="format === 'basic'" class="tip-tap-controls">
      <div class="row mx-0 align-items-center px-1">
        <button id="source_mode" :class="{ 'rs-text-primary': isSourceMode }" @click="handleSourceModeToggle">
          <i class="far fa-code mr-1" />Source
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button
          :disabled="!editor.can().chain().focus().toggleBold().run()"
          :class="{ 'is-active': editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()"
        >
          <i class="far fa-bold" />
        </button>

        <button
          :disabled="!editor.can().chain().focus().toggleItalic().run()"
          :class="{ 'is-active': editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <i class="far fa-italic" />
        </button>

        <button
          :disabled="!editor.can().chain().focus().toggleUnderline().run()"
          :class="{ 'is-active': editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          <i class="far fa-underline" />
        </button>

        <button @click="editor.chain().focus().clearNodes().run(); editor.chain().focus().unsetAllMarks().run()">
          <i class="far fa-remove-format" />
        </button>

        <button @click="editor.chain().focus().setHorizontalRule().run()">
          <i class="far fa-horizontal-rule" />
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button
          :class="{ 'is-active': editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
        >
          <i class="far fa-list-ol" />
        </button>

        <button
          :class="{ 'is-active': editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
        >
          <i class="far fa-list" />
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button
          :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
          @click="editor.chain().focus().toggleTextAlign('left').run()"
        >
          <i class="far fa-align-left" />
        </button>

        <button
          :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }"
          @click="editor.chain().focus().toggleTextAlign('center').run()"
        >
          <i class="far fa-align-center" />
        </button>

        <button
          :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }"
          @click="editor.chain().focus().toggleTextAlign('right').run()"
        >
          <i class="far fa-align-right" />
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button :disabled="!hasSelection" @click="handleNewLink()">
          <i class="far fa-link" />
        </button>

        <span class="separator border-right mx-1">&nbsp;</span>

        <button :disabled="!editor.can().chain().focus().undo().run()" @click="editor.chain().focus().undo().run()">
          <i class="far fa-undo" />
        </button>

        <button :disabled="!editor.can().chain().focus().redo().run()" @click="editor.chain().focus().redo().run()">
          <i class="far fa-redo" />
        </button>

        <span v-if="!isFullScreen" class="separator border-right mx-1">&nbsp;</span>

        <button v-if="!isFullScreen" @click="handleMaximize">
          <i class="far fa-maximize" />
        </button>
      </div>
    </div>

    <!-- Tiny Editor -->
    <div v-else class="tip-tap-controls">
      <div class="row mx-0 align-items-center px-1">
        <button
          :disabled="!editor.can().chain().focus().toggleBold().run()"
          :class="{ 'is-active': editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()"
        >
          <i class="far fa-bold" />
        </button>

        <button
          :disabled="!editor.can().chain().focus().toggleItalic().run()"
          :class="{ 'is-active': editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <i class="far fa-italic" />
        </button>

        <button
          :disabled="!editor.can().chain().focus().toggleUnderline().run()"
          :class="{ 'is-active': editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          <i class="far fa-underline" />
        </button>
      </div>
    </div>

    <div v-if="!isSourceMode">
      <!-- Table Editor -->
      <RsPartialTipTapTableEditor
        v-if="activeStates.includes('table')"
        :editor="editor"
      />

      <!-- Link Editor -->
      <RsPartialTipTapLinkEditor
        v-if="activeStates.includes('link')"
        :editor="editor"
        :on-file-upload-success="onFileUploadSuccess"
        :on-file-upload-start="onFileUploadStart"
        :on-file-upload-error="onFileUploadError"
      />

      <!-- Image Editor -->
      <RsPartialTipTapImageEditor
        v-if="activeStates.includes('image')"
        :editor="editor"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Editor } from '@tiptap/vue-2';
import { defineComponent, PropType } from 'vue';
import { BFormSelect } from 'bootstrap-vue';
import {
  editableImage,
  editableTable,
  textFormats,
  fontFamilies,
} from '@/shared/tiptap/consts';
import RsPartialTipTapLinkEditor from './RsPartialTipTapLinkEditor.vue';
import RsPartialTipTapTableEditor from './RsPartialTipTapTableEditor.vue';
import RsPartialTipTapImageEditor from './RsPartialTipTapImageEditor.vue';
import { uploadWithFileDialog } from '@/shared/tiptap/extensions/file-handler';
import { TTipTapFormat } from '@/shared/tiptap/types';

export default defineComponent({
  components: {
    RsPartialTipTapLinkEditor,
    RsPartialTipTapTableEditor,
    RsPartialTipTapImageEditor,
  },

  props: {
    /**
     * TipTap Editor instance passed by parent component
     */
    editor: {
      type: Object as PropType<Editor>,
      required: true,
    },

    /**
     * Determines what controls we display on the toolbar.
     */
    format: {
      type: String as PropType<TTipTapFormat>,
      default: 'full',
    },

    /**
     * If this toolbar is in full screen mode, hides the full screen toggle button.
     */
    isFullScreen: {
      type: Boolean,
      default: false,
    },

    onFullScreenToggle: {
      type: Function as PropType<(editor: Editor) => void>,
      default: () => () => {},
    },

    onSourceModeToggle: {
      type: Function as PropType<(value: boolean) => void>,
      default: () => () => {},
    },

    /**
     * Callback function for file upload start, allowing parent component to do
     * something when a file upload has commenced on the toolbar.
     */
    onFileUploadStart: {
      type: Function as PropType<() => void>,
      default: () => () => {},
    },

    /**
     * Callback function for file upload success, allowing parent component to do
     * something when a file upload has succeeded on the toolbar.
     */
    onFileUploadSuccess: {
      type: Function as PropType<() => void>,
      default: () => () => {},
    },

    /**
     * Callback function for file upload errors, allowing parent component to do
     * something when a file upload has fails on the toolbar.
     */
    onFileUploadError: {
      type: Function as PropType<() => void>,
      default: () => () => {},
    },
  },

  data() {
    return {
      isSourceMode: false,
      tableWidthUoM: '%',
      currentFormat: textFormats[0].value,
      currentFont: fontFamilies[0].value,
      textFormats,
      fontFamilies,
      activeStates: [] as string[],
    };
  },

  computed: {
    /**
     * Returns boolean if the editor has some sort of selected text range.
     */
    hasSelection() {
      const { to, from } = this.editor.state.selection;
      return to - from > 0;
    },
  },

  watch: {
    'editor.state.selection': {
      deep: true,
      handler() {
        // Don't immediately change state when editor is out of focus. We might be editing.
        if (this.editor?.isFocused) {
          this.checkFormatActiveState();
          this.checkFontActiveStates();

          const activeStates: Record<string, boolean> = {
            image: this.editor?.isActive('image'),
            link: this.editor?.isActive('link'),
            table: this.editor?.isActive('table'),
          };

          this.activeStates = Object.keys(activeStates).filter((key) => activeStates[key]);
        }
      },
    },
  },

  methods: {
    /**
     * For text format select box,
     * Ensures clicking on an area of the editor sets the correct select option for text type at the position.
     */
    checkFormatActiveState() {
      const activeStates: Record<string, boolean> = {
        paragraph: this.editor.isActive('paragraph'),
        'heading.1': this.editor.isActive('heading', { level: 1 }),
        'heading.2': this.editor.isActive('heading', { level: 2 }),
        'heading.3': this.editor.isActive('heading', { level: 3 }),
      };

      this.currentFormat = Object.keys(activeStates).find((key) => (activeStates[key])) ?? 'paragraph';
    },

    /**
     * For font family select box,
     * Ensures clicking on an area of the editor sets the correct select option for font family at the position.
     */
    checkFontActiveStates() {
      const activeStates: Record<string, boolean> = {
        'sans-serif': this.editor.isActive('textStyle', { fontFamily: 'Arial, sans-serif' }),
        serif: this.editor.isActive('textStyle', { fontFamily: `'Times New Roman', Times, serif` }),
        monospace: this.editor.isActive('textStyle', { fontFamily: `'Courier New', Courier, monospace` }),
      };

      this.currentFont = Object.keys(activeStates).find((key) => (activeStates[key])) ?? 'sans-serif';
    },

    /**
     * Uploads an image to S3 userfiles and adds it to the editor's current selection.
     * Calls passed callback methods for start, success, and fail states (if defined)
     */
    async handleAddImage() {
      await uploadWithFileDialog({
        onUploadStart: () => this.onFileUploadStart(),
        onUploadSuccess: (filename) => {
          const imageProps = {
            ...editableImage,
            src: filename,
          };

          this.editor
            .chain()
            .focus()
            .setImage(imageProps)
            .run();

          this.onFileUploadSuccess();
        },
        onUploadError: () => this.onFileUploadError(),
        fileRestrict: 'image',
      });
    },

    /**
     * Adds a new table and sets default attributes to the editor's current selection.
     */
    handleAddTable() {
      this.editor
        .chain()
        .focus()
        .insertTable({
          rows: 3,
          cols: 3,
          withHeaderRow: true,
        })
        .run();

      this.editor
        .chain()
        .focus()
        .updateAttributes('table', editableTable)
        .run();
    },

    /**
     * Adds a new link to the editor's current selection.
     */
    handleNewLink() {
      this.editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({
          target: '_blank',
          href: 'https://example.com',
        })
        .run();
    },

    /**
     * Handler for select value changed in the text formatting select box.
     * Updates selected text / section to set formatting (header, paragraphs, etc.)
     * @param value
     */
    handleTextFormatChange(value: string) {
      const setState: Record<string, () => boolean> = {
        paragraph: () => this.editor.chain().focus().setParagraph().run(),
        'heading.1': () => this.editor.chain().focus().toggleHeading({ level: 1 }).run(),
        'heading.2': () => this.editor.chain().focus().toggleHeading({ level: 2 }).run(),
        'heading.3': () => this.editor.chain().focus().toggleHeading({ level: 3 }).run(),
      };

      if (setState[value]) {
        setState[value]();
      }
    },

    /**
     * Handler for select value changed in the font family select box.
     * Updates selected text / section to set font family
     * @param value
     */
    handleFontFamilyChange(value: string) {
      const setState: Record<string, () => boolean> = {
        'sans-serif': () => this.editor.chain().focus().setFontFamily('Arial, sans-serif').run(),
        serif: () => this.editor.chain().focus().setFontFamily(`'Times New Roman', Times, serif`).run(),
        monospace: () => this.editor.chain().focus().setFontFamily(`'Courier New', Courier, monospace`).run(),
      };

      if (setState[value]) {
        setState[value]();
      }
    },

    /**
     * Handles full screen mode button click.
     * Calls the default or passed `onFullScreenToggle` callback function prop.
     */
    handleMaximize() {
      if (this.onFullScreenToggle) {
        this.onFullScreenToggle(this.editor);
      }
    },

    /**
     * Handles source mode button click.
     * Calls the default or passed `onSourceModeToggle` callback function prop.
     * Additionally unsets editable states and disables buttons on the toolbar if source mode is active.
     */
    handleSourceModeToggle() {
      this.isSourceMode = !this.isSourceMode;

      if (this.onSourceModeToggle) {
        this.onSourceModeToggle(this.isSourceMode);
      }

      const container = this.$refs.tipTapToolbarContainer;

      if (container instanceof HTMLDivElement) {
        const controls = [
          ...container.querySelectorAll('button'),
          ...container.querySelectorAll('select'),
          ...container.querySelectorAll('input'),
        ];

        controls.forEach((c) => {
          if (c.id === 'source_mode') {
            return;
          }

          if (this.isSourceMode) {
            c.setAttribute('disabled', 'true');
            return;
          }

          c.removeAttribute('disabled');
        });
      }
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";

.rs-partial-tip-tap-toolbar {
  select, button, option, input, label, a {
    font-size: 14px;
    font-weight: normal;
  }

  label {
    margin-bottom: 0px;
  }

  input {
    height: auto;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .tip-tap-controls {
    button {
      padding: 2px 4px;
      margin: 4px;

      &:hover {
        color: $primary-color;
      }

      &.is-active {
        color: $primary-color;
      }

      &:disabled {
        color: $disabled;
      }
    }
  }
}
</style>
